import { MakeLogicType, actions, connect, kea, listeners, path, props, reducers } from 'kea'
import { forms } from 'kea-forms'
import type { DeepPartial, DeepPartialMap, FieldName, ValidationErrorType } from 'kea-forms'

import api from 'lib/api'
import { integrationsLogic } from 'lib/integrations/integrationsLogic'
import { lemonToast } from 'lib/lemon-ui/LemonToast'

import { IntegrationType } from '~/types'

import {
    PushIdentityVerificationMode,
    pushIdentityPublicKeysPayload,
    pushIdentityVerificationPayload,
    resolvePushIdentityPublicKey,
    resolvePushIdentityVerification,
} from '../PushIdentityVerificationField'

export interface APNSSetupModalLogicProps {
    integration?: IntegrationType | null
    onComplete: (integrationId?: number) => void
    onClose: () => void
}

export interface APNSFormType {
    signingKey: string
    keyId: string
    teamId: string
    bundleId: string
    environment: 'production' | 'sandbox'
    identityVerification: PushIdentityVerificationMode
    identityPublicKey: string
}

// Apple hands out the signing key as a .p8 file, so accepting an upload saves opening it in an
// editor to copy the PEM out. Both routes populate the same `signingKey` field.
const PEM_MARKER = 'PRIVATE KEY'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface apnsSetupModalLogicValues {
    apnsIntegration: {
        bundleId: string
        environment: 'production'
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        keyId: string
        signingKey: string
        teamId: string
    }
    apnsIntegrationAllErrors: Record<string, any>
    apnsIntegrationChanged: boolean
    apnsIntegrationErrors: DeepPartialMap<
        {
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        },
        ValidationErrorType
    >
    apnsIntegrationHasErrors: boolean
    apnsIntegrationManualErrors: Record<string, any>
    apnsIntegrationTouched: boolean
    apnsIntegrationTouches: Record<string, boolean>
    apnsIntegrationValidationErrors: DeepPartialMap<
        {
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        },
        ValidationErrorType
    >
    isApnsIntegrationSubmitting: boolean
    isApnsIntegrationValid: boolean
    showApnsIntegrationErrors: boolean
    signingKeyFileError: string | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface apnsSetupModalLogicActions {
    loadIntegrations: () => any // integrationsLogic
    resetApnsIntegration: (values?: {
        bundleId: string
        environment: 'production'
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        keyId: string
        signingKey: string
        teamId: string
    }) => {
        values?: {
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        }
    }
    setApnsIntegrationManualErrors: (errors: Record<string, any>) => {
        errors: Record<string, any>
    }
    setApnsIntegrationValue: (
        key: FieldName,
        value: any
    ) => {
        name: FieldName
        value: any
    }
    setApnsIntegrationValues: (
        values: DeepPartial<{
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        }>
    ) => {
        values: DeepPartial<{
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        }>
    }
    setSigningKeyFileError: (error: string | null) => {
        error: string | null
    }
    setSigningKeyFiles: (files: File[]) => {
        files: File[]
    }
    submitApnsIntegration: () => {
        value: boolean
    }
    submitApnsIntegrationFailure: (
        error: Error,
        errors: Record<string, any>
    ) => {
        error: Error
        errors: Record<string, any>
    }
    submitApnsIntegrationRequest: (apnsIntegration: {
        bundleId: string
        environment: 'production'
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        keyId: string
        signingKey: string
        teamId: string
    }) => {
        apnsIntegration: {
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        }
    }
    submitApnsIntegrationSuccess: (apnsIntegration: {
        bundleId: string
        environment: 'production'
        identityPublicKey: string
        identityVerification: PushIdentityVerificationMode
        keyId: string
        signingKey: string
        teamId: string
    }) => {
        apnsIntegration: {
            bundleId: string
            environment: 'production'
            identityPublicKey: string
            identityVerification: PushIdentityVerificationMode
            keyId: string
            signingKey: string
            teamId: string
        }
    }
    touchApnsIntegrationField: (key: string) => {
        key: string
    }
}

export type apnsSetupModalLogicType = MakeLogicType<
    apnsSetupModalLogicValues,
    apnsSetupModalLogicActions,
    APNSSetupModalLogicProps
>

export const apnsSetupModalLogic = kea<apnsSetupModalLogicType>([
    path(['products', 'workflows', 'frontend', 'APNSSetup', 'apnsSetupModalLogic']),
    props({} as APNSSetupModalLogicProps),
    connect(() => ({
        actions: [integrationsLogic, ['loadIntegrations']],
    })),
    actions({
        setSigningKeyFiles: (files: File[]) => ({ files }),
        setSigningKeyFileError: (error: string | null) => ({ error }),
    }),
    reducers({
        signingKeyFileError: [
            null as string | null,
            {
                setSigningKeyFileError: (_, { error }) => error,
                setSigningKeyFiles: () => null,
            },
        ],
    }),
    listeners(({ actions }) => ({
        setSigningKeyFiles: async ({ files }, breakpoint) => {
            const file = files[0]
            if (!file) {
                return
            }
            let contents: string
            try {
                contents = await file.text()
            } catch {
                actions.setSigningKeyFileError("Couldn't read that file. Try again, or paste the key contents instead.")
                return
            }
            // Picking a second file while this read is in flight makes this result stale. Without the
            // breakpoint whichever read finished last would win, so an earlier file could overwrite the
            // key while the input shows the newer filename.
            breakpoint()
            if (!contents.includes(PEM_MARKER)) {
                actions.setSigningKeyFileError(
                    "That file doesn't look like a signing key. Upload the .p8 file you downloaded from Apple."
                )
                return
            }
            actions.setApnsIntegrationValue('signingKey', contents.trim())
        },
    })),
    forms(({ props, actions, values }) => ({
        apnsIntegration: {
            defaults: {
                signingKey: '',
                keyId: '',
                teamId: '',
                bundleId: '',
                environment: 'production' as const,
                identityVerification: resolvePushIdentityVerification(props.integration),
                identityPublicKey: resolvePushIdentityPublicKey(props.integration),
            },
            errors: ({ signingKey, keyId, teamId, bundleId }) => ({
                signingKey: signingKey.trim() ? undefined : 'Signing key is required',
                keyId: keyId.trim() ? undefined : 'Key ID is required',
                teamId: teamId.trim() ? undefined : 'Team ID is required',
                bundleId: bundleId.trim() ? undefined : 'Bundle ID is required',
            }),
            submit: async () => {
                try {
                    const integration = await api.integrations.create({
                        kind: 'apns',
                        config: {
                            signing_key: values.apnsIntegration.signingKey,
                            key_id: values.apnsIntegration.keyId,
                            team_id_apple: values.apnsIntegration.teamId,
                            bundle_id: values.apnsIntegration.bundleId,
                            environment: values.apnsIntegration.environment,
                            ...pushIdentityVerificationPayload(
                                values.apnsIntegration.identityVerification,
                                props.integration
                            ),
                            ...pushIdentityPublicKeysPayload(
                                values.apnsIntegration.identityPublicKey,
                                props.integration
                            ),
                        },
                    })
                    actions.loadIntegrations()
                    lemonToast.success('APNS channel created successfully!')
                    props.onComplete(integration.id)
                } catch (error: any) {
                    lemonToast.error(error.detail || 'Failed to create APNS channel')
                    throw error
                }
            },
        },
    })),
])
